import { Accordion, Card, Row, Col, Modal, Form } from "react-bootstrap";
import Buttons from "../../components/buttons/Buttons";
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Styles from "../lets_talk/Letstalk.module.css";
import SearchInput from "../../components/searchinput/SearchInput";
import DataTable from "../../components/datatable/DataTable";
import Pagination from "../../components/pagination/Pagination";
import moment from "moment";
import dayjs from "dayjs";
import isSameOrOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { AxiosInstance } from "../../axios";
import Cookies from "js-cookie";
import ToastMessage from "../../components/messagetoast/ToastMessage";
import stylesview from "./JobView.module.css";

const Job_View = () => {
  dayjs.extend(isSameOrAfter);
  dayjs.extend(isSameOrOrBefore);
  dayjs.extend(customParseFormat);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const jobId = location.state?.jobId;

  const jobpage = "job_view";
  const user_type_cookie = Cookies.get("user_type");

  const [jobDetails, setJobDetails] = useState(null);
  const [allApplicants, setAllApplicants] = useState([]);

  // Initialize filters from URL params - REFACTORED INTO SINGLE STATE
  const getInitialFilters = () => ({
    search: searchParams.get("search") || "",
    candidateStatus: searchParams.get("candidateStatus") || "",
    willingToRelocate: searchParams.get("willingToRelocate") || "",
    fromDate: searchParams.get("fromDate") || "",
    toDate: searchParams.get("toDate") || "",
  });

  const initialCurrentPage = Number(searchParams.get("page")) || 1;
  const initialRowsPerPage = Number(searchParams.get("limit")) || 10;

  // Main filter state - CONSOLIDATED
  const [filters, setFilters] = useState(getInitialFilters());

  // Temporary states for filter popups - CONSOLIDATED
  const [tempFilters, setTempFilters] = useState({
    candidateStatus: "",
    willingToRelocate: "",
    fromDate: "",
    toDate: "",
    dateLabel: "",
  });

  const [chipData, setChipData] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(initialCurrentPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const rowsPerPageOptions = [10, 15, 20, 50];

  const [showJobStatusModal, setShowJobStatusModal] = useState(false);
  const [showApprovalStatusModal, setShowApprovalStatusModal] = useState(false);
  const [selectedJobStatus, setSelectedJobStatus] = useState("");
  const [selectedApprovalStatus, setSelectedApprovalStatus] = useState("");

  const [isOpen, setIsOpen] = useState({ status: false, column: "" });
  const filterRef = useRef(null);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "success",
  });

  // DataTable Configuration / Filter Options
  const filterOptions = useMemo(
    () => ({
      status: [
        { label: "Pending", value: "Pending" },
        { label: "Reviewing", value: "Reviewing" },
        { label: "Shortlisted", value: "Shortlisted" },
        { label: "Scheduled", value: "Scheduled" },
        { label: "On Hold", value: "On Hold" },
        { label: "Offered", value: "Offered" },
        { label: "Rejected", value: "Rejected" },
      ],
      willing_to_relocate: [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" },
      ],
    }),
    [],
  );

  const columns = useMemo(
    () => [
      { header: "S.No", accessor: "sno" },
      { header: "Name", accessor: "full_name", sortable: true },
      { header: "Email Address", accessor: "email", sortable: true },
      { header: "Phone Number", accessor: "phone_number", sortable: true },
      {
        header: "Total Experience",
        accessor: "total_experience",
        sortable: true,
      },
      {
        header: "Willing to Relocate",
        accessor: "willing_to_relocate",
        sortable: true,
        filterable: true,
      },
      {
        header: "Applied Date",
        accessor: "applied_date",
        sortable: true,
        filterable: true,
        isDateColumn: true,
      },
      {
        header: "Status",
        accessor: "status",
        sortable: true,
        filterable: true,
      },
    ],
    [],
  );

  // Helper Functions
  const getWorkArrangementLabel = useCallback((value) => {
    const numericValue = parseInt(value, 10);
    switch (numericValue) {
      case 1:
        return "On-site";
      case 2:
        return "Remote";
      case 3:
        return "Hybrid";
      default:
        return "-";
    }
  }, []);

  const getJobStatusNumericalValue = useCallback((label) => {
    switch (label) {
      case "Waiting for Approval":
        return 0;
      case "Inactive":
        return 1;
      case "Active":
        return 2;
      default:
        return null;
    }
  }, []);

  const getApprovalStatusNumericalValue = useCallback((label) => {
    switch (label) {
      case "Waiting for Approval":
        return 0;
      case "On Hold":
        return 1;
      case "Approved":
        return 2;
      case "Reject":
        return 3;
      default:
        return null;
    }
  }, []);

  const getCandidateStatusNumericalValue = useCallback((label) => {
    switch (label) {
      case "Pending":
        return 0;
      case "Reviewing":
        return 1;
      case "Shortlisted":
        return 2;
      case "Scheduled":
        return 3;
      case "On Hold":
        return 4;
      case "Offered":
        return 5;
      case "Rejected":
        return 6;
      default:
        return null;
    }
  }, []);

  const getWillingToRelocateNumericalValue = useCallback((label) => {
    switch (label) {
      case "Yes":
        return 1;
      case "No":
        return 0;
      default:
        return null;
    }
  }, []);

  // API Call Functions
  const fetchJobDetails = useCallback(async (currentJobId) => {
    if (!currentJobId) {
      console.warn("No job ID provided for fetching job details.");
      setJobDetails(null);
      return;
    }
    try {
      const jobRes = await AxiosInstance.post("/api/joinourteam/job/view", {
        job_id: currentJobId,
      });

      if (jobRes?.data?.job) {
        setJobDetails(jobRes.data.job);
        setSelectedJobStatus(["Inactive"][jobRes.data.job.job_status] || "");
        setSelectedApprovalStatus(
          ["Waiting for Approval", "On Hold", "Approved", "Reject"][
            jobRes.data.job.approval_status
          ] || "",
        );
      } else {
        setJobDetails(null);
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
      setJobDetails(null);
    }
  }, []);

  const fetchApplicants = useCallback(
    async (currentJobId, currentFilters) => {
      if (!currentJobId) {
        console.warn("No job ID provided for fetching applicants.");
        setAllApplicants([]);
        return;
      }
      try {
        const payload = {
          job_id: currentJobId,
          search: currentFilters.search || "",
          status:
            currentFilters.candidateStatus !== ""
              ? getCandidateStatusNumericalValue(currentFilters.candidateStatus)
              : null,
          is_relocated:
            currentFilters.willingToRelocate !== ""
              ? getWillingToRelocateNumericalValue(
                  currentFilters.willingToRelocate,
                )
              : null,
          start_date: currentFilters.fromDate
            ? dayjs(currentFilters.fromDate, "DD/MM/YYYY").format(
                "YYYY-MM-DD 00:00:00",
              )
            : null,
          end_date: currentFilters.toDate
            ? dayjs(currentFilters.toDate, "DD/MM/YYYY")
                .endOf("day")
                .format("YYYY-MM-DD HH:mm:ss")
            : null,
        };

        // Remove null values from payload
        Object.keys(payload).forEach(
          (key) => payload[key] === null && delete payload[key],
        );


        const applicantsRes = await AxiosInstance.post(
          "/api/joinourteam/candidate/list",
          payload,
        );

        const applicantList = Array.isArray(applicantsRes?.data?.Candidates)
          ? applicantsRes.data.Candidates
          : [];
        setAllApplicants(applicantList);
      } catch (error) {
        console.error("Error fetching applicants:", error);
        setAllApplicants([]);
      }
    },
    [getCandidateStatusNumericalValue, getWillingToRelocateNumericalValue],
  );

  // Handlers for Filters and Pagination - REFACTORED
  const handleSearch = useCallback((value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setCurrentPage(1);
  }, []);

  const handleDeleteChip = useCallback((chipToDelete) => {
    setFilters((prev) => {
      const updated = { ...prev };
      if (chipToDelete.type === "search") {
        updated.search = "";
      }
      if (chipToDelete.type === "status") {
        updated.candidateStatus = "";
      }
      if (chipToDelete.type === "willing_to_relocate") {
        updated.willingToRelocate = "";
      }
      if (chipToDelete.type === "applied_date") {
        updated.fromDate = "";
        updated.toDate = "";
      }
      return updated;
    });

    setTempFilters({
      candidateStatus: "",
      willingToRelocate: "",
      fromDate: "",
      toDate: "",
      dateLabel: "",
    });

    setCurrentPage(1);
  }, []);

  const handleAllClear = useCallback(() => {
    setFilters({
      search: "",
      candidateStatus: "",
      willingToRelocate: "",
      fromDate: "",
      toDate: "",
    });

    setTempFilters({
      candidateStatus: "",
      willingToRelocate: "",
      fromDate: "",
      toDate: "",
      dateLabel: "",
    });

    setCurrentPage(1);
  }, []);

  const handleFilterClick = useCallback(
    (columnAccessor) => {
      setIsOpen({ status: true, column: columnAccessor });

      if (columnAccessor === "status") {
        setTempFilters((prev) => ({
          ...prev,
          candidateStatus: filters.candidateStatus,
        }));
      } else if (columnAccessor === "willing_to_relocate") {
        setTempFilters((prev) => ({
          ...prev,
          willingToRelocate: filters.willingToRelocate,
        }));
      } else if (columnAccessor === "applied_date") {
        setTempFilters((prev) => ({
          ...prev,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
        }));
        const currentChip = chipData.find(
          (chip) => chip.type === "applied_date",
        );
        setTempFilters((prev) => ({
          ...prev,
          dateLabel: currentChip ? currentChip.label : "",
        }));
      }
    },
    [filters, chipData],
  );

  const handleApplyFilter = useCallback(() => {
    setFilters((prev) => {
      const updated = { ...prev };
      if (isOpen.column === "status") {
        updated.candidateStatus = tempFilters.candidateStatus;
      } else if (isOpen.column === "willing_to_relocate") {
        updated.willingToRelocate = tempFilters.willingToRelocate;
      }
      return updated;
    });
    setIsOpen({ status: false, column: "" });
    setCurrentPage(1);
  }, [isOpen.column, tempFilters]);

  const handleClearSpecificFilter = useCallback((columnAccessor) => {
    setFilters((prev) => {
      const updated = { ...prev };
      if (columnAccessor === "status") {
        updated.candidateStatus = "";
      } else if (columnAccessor === "willing_to_relocate") {
        updated.willingToRelocate = "";
      } else if (columnAccessor === "applied_date") {
        updated.fromDate = "";
        updated.toDate = "";
      }
      return updated;
    });

    setTempFilters({
      candidateStatus: "",
      willingToRelocate: "",
      fromDate: "",
      toDate: "",
      dateLabel: "",
    });

    setIsOpen({ status: false, column: "" });
    setCurrentPage(1);
  }, []);

  const handleDateRangeApply = useCallback(
    ({ fromDate: selectedFrom, toDate: selectedTo, label: selectedLabel }) => {
      setFilters((prev) => ({
        ...prev,
        fromDate: selectedFrom,
        toDate: selectedTo,
      }));

      setTempFilters((prev) => ({
        ...prev,
        fromDate: selectedFrom,
        toDate: selectedTo,
        dateLabel: selectedLabel,
      }));

      setIsOpen({ status: false, column: "" });
      setCurrentPage(1);
    },
    [],
  );

  const handleClickOutside = useCallback(
    (event) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        !event.target.closest('img[alt="filter"]') &&
        !event.target.closest("button[data-filter-trigger]")
      ) {
        setIsOpen((prev) => {
          if (prev.status) {
            setTempFilters({
              candidateStatus: filters.candidateStatus,
              willingToRelocate: filters.willingToRelocate,
              fromDate: filters.fromDate,
              toDate: filters.toDate,
              dateLabel: "",
            });
          }
          return { ...prev, status: false };
        });
      }
    },
    [filters],
  );

  // useEffect Hooks

  // Effect to update URL search parameters when filter or pagination states change
  // Effect to update URL search parameters when filter or pagination states change
  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.search) params.set("search", filters.search);
    if (filters.candidateStatus)
      params.set("candidateStatus", filters.candidateStatus);
    if (filters.willingToRelocate)
      params.set("willingToRelocate", filters.willingToRelocate);
    if (filters.fromDate) params.set("fromDate", filters.fromDate);
    if (filters.toDate) params.set("toDate", filters.toDate);
    if (currentPage !== 1) params.set("page", currentPage.toString());
    if (rowsPerPage !== 10) params.set("limit", rowsPerPage.toString());

    const newSearchString = params.toString();
    const currentSearchString = location.search.substring(1); // Remove '?'

    // FIX: Only navigate if the calculated search string is different
    if (newSearchString !== currentSearchString) {
      navigate(`?${newSearchString}`, { replace: true, state: location.state });
    } else {
    }
  }, [
    filters,
    currentPage,
    rowsPerPage,
    navigate,
    location.state,
    location.search,
  ]);

  // Effect to fetch job details and ALL applicants based on filters
  useEffect(() => {
    if (jobId) {
      fetchJobDetails(jobId);
      fetchApplicants(jobId, filters);
    } else {
      console.error(
        "Job ID not found in location state. Cannot fetch job details or applicants.",
      );
    }
  }, [jobId, fetchJobDetails, fetchApplicants, filters]);

  // Effect to update chip data based on active filters
  useEffect(() => {
    const currentChips = [];
    if (filters.search) {
      currentChips.push({
        type: "search",
        value: filters.search,
        label: `Search: "${filters.search}"`,
      });
    }
    if (filters.candidateStatus) {
      const statusOption = filterOptions.status.find(
        (option) => option.value === filters.candidateStatus,
      );
      if (statusOption) {
        currentChips.push({
          label: `Status: ${statusOption.label}`,
          type: "status",
          value: filters.candidateStatus,
        });
      }
    }
    if (filters.willingToRelocate) {
      const relocateOption = filterOptions.willing_to_relocate.find(
        (option) => option.value === filters.willingToRelocate,
      );
      if (relocateOption) {
        currentChips.push({
          label: `Willing to Relocate: ${relocateOption.label}`,
          type: "willing_to_relocate",
          value: filters.willingToRelocate,
        });
      }
    }
    if (filters.fromDate || filters.toDate) {
      let label = "";
      if (filters.fromDate && filters.toDate) {
        label = `Applied Date: ${filters.fromDate} - ${filters.toDate}`;
      } else if (filters.fromDate) {
        label = `Applied From: ${filters.fromDate}`;
      } else if (filters.toDate) {
        label = `Applied To: ${filters.toDate}`;
      }
      currentChips.push({
        label: label,
        type: "applied_date",
        value: `${filters.fromDate}-${filters.toDate}`,
      });
    }
    setChipData(currentChips);
  }, [filters, filterOptions.status, filterOptions.willing_to_relocate]);
  // Restore filters when coming back from candidates-view
  useEffect(() => {
    if (location.state?.from === "candidates-view" && location.state?.filters) {
      setFilters(location.state.filters); // ✅ set filters from state
    }
  }, [location.state]);

  // Effect for handling clicks outside the filter popup
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  // Calculate total items from the filtered data
  const totalFilteredItems = allApplicants.length;

  // Client-Side Pagination Logic
  const paginatedApplicants = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return allApplicants.slice(startIndex, endIndex);
  }, [allApplicants, currentPage, rowsPerPage]);

  // Processed applicants for DataTable
  const processedApplicants = useMemo(
    () =>
      paginatedApplicants.map((applicant, index) => {
        let statusText = "-";
        switch (applicant.status) {
          case 0:
            statusText = "Pending";
            break;
          case 1:
            statusText = "Reviewing";
            break;
          case 2:
            statusText = "Shortlisted";
            break;
          case 3:
            statusText = "Scheduled";
            break;
          case 4:
            statusText = "On Hold";
            break;
          case 5:
            statusText = "Offered";
            break;
          case 6:
            statusText = "Rejected";
            break;
          default:
            statusText = applicant.status || "-";
        }

        return {
          sno: (currentPage - 1) * rowsPerPage + index + 1,
          full_name: applicant.full_name || "-",
          email: applicant.email || "-",
          phone_number: applicant.phone_number || "-",
          total_experience: applicant.yearof_experience || "-",
          willing_to_relocate:
            applicant.is_relocated === 1
              ? "Yes"
              : applicant.is_relocated === 0
                ? "No"
                : "-",
          applied_date: applicant.applied_date
            ? moment(applicant.applied_date).format("DD/MM/YYYY")
            : "-",
          status: statusText,
        };
      }),
    [paginatedApplicants, currentPage, rowsPerPage],
  );

  // Modal Handlers (Job Status & Approval Status)
  const handleShowJobStatusModal = () => setShowJobStatusModal(true);
  const handleCloseJobStatusModal = () => setShowJobStatusModal(false);

  const handleSaveJobStatus = async () => {
    if (!jobDetails || !jobDetails.job_id) {
      console.error("Job details not available to update status.");
      setToast({
        show: true,
        message: "Error: Job details not available.",
        variant: "danger",
      });
      return;
    }

    const numericalJobStatus = getJobStatusNumericalValue(selectedJobStatus);
    if (numericalJobStatus === null) {
      console.error("Invalid job status selected.");
      setToast({
        show: true,
        message: "Error: Invalid job status selected.",
        variant: "danger",
      });
      return;
    }

    try {
      const tagIds = jobDetails.tag_id
        ? jobDetails.tag_id.split(",").filter((id) => id)
        : [];
      const tagNames = jobDetails.tag_name
        ? jobDetails.tag_name.split(",").filter((name) => name)
        : [];

      const tagArray = tagNames.map((name, index) => ({
        tag_id: tagIds[index] || "",
        tag_name: name,
      }));

      const payload = {
        user_type: parseInt(user_type_cookie, 10),
        job_id: jobDetails.job_id,
        role: jobDetails.role,
        department_id: parseInt(jobDetails.department_id, 10),
        location: jobDetails.location,
        yearof_experience: jobDetails.yearof_experience,
        description: jobDetails.description,
        work_arrangement: parseInt(jobDetails.work_arrangement, 10),
        tag: tagArray,
        updated_by: Cookies.get("user_id"),
        is_active: jobDetails.is_active || 1,
        deleted: jobDetails.deleted || 0,
        job_status: numericalJobStatus,
        approval_status: jobDetails.approval_status,
      };

      const response = await AxiosInstance.post(
        "/api/joinourteam/job/update",
        payload,
      );
      console.log("Job Status updated successfully:", response.data);
      setToast({
        show: true,
        message: "Job status updated successfully!",
        variant: "success",
      });
      fetchJobDetails(jobDetails.job_id);
      handleCloseJobStatusModal();
    } catch (error) {
      console.error("Error updating job status:", error);
      setToast({
        show: true,
        message: `Failed to update job status: ${
          error.response?.data?.message || error.message
        }`,
        variant: "danger",
      });
    }
  };

  const handleJobStatusRadioChange = (e) => {
    setSelectedJobStatus(e.target.value);
  };

  const handleShowApprovalStatusModal = () => setShowApprovalStatusModal(true);
  const handleCloseApprovalStatusModal = () =>
    setShowApprovalStatusModal(false);

  const handleSaveApprovalStatus = async () => {
    if (!jobDetails || !jobDetails.job_id) {
      console.error("Job details not available to update status.");
      setToast({
        show: true,
        message: "Error: Job details not available.",
        variant: "danger",
      });
      return;
    }

    const numericalApprovalStatus = getApprovalStatusNumericalValue(
      selectedApprovalStatus,
    );
    if (numericalApprovalStatus === null) {
      console.error("Invalid approval status selected.");
      setToast({
        show: true,
        message: "Error: Invalid approval status selected.",
        variant: "danger",
      });
      return;
    }

    let jobStatusToSend = jobDetails.job_status;
    switch (numericalApprovalStatus) {
      case 2:
        jobStatusToSend = 2;
        break;
      case 3:
        jobStatusToSend = 1;
        break;
      default:
        break;
    }

    try {
      const tagIds = jobDetails.tag_id
        ? jobDetails.tag_id.split(",").filter((id) => id)
        : [];
      const tagNames = jobDetails.tag_name
        ? jobDetails.tag_name.split(",").filter((name) => name)
        : [];

      const tagArray = tagNames.map((name, index) => ({
        tag_id: tagIds[index] || "",
        tag_name: name,
      }));

      const payload = {
        user_type: parseInt(jobDetails.user_type, 10),
        job_id: jobDetails.job_id,
        role: jobDetails.role,
        department_id: parseInt(jobDetails.department_id, 10),
        location: jobDetails.location,
        yearof_experience: jobDetails.yearof_experience,
        description: jobDetails.description,
        work_arrangement: parseInt(jobDetails.work_arrangement, 10),
        tag: tagArray,
        updated_by: Cookies.get("user_id"),
        is_active: jobDetails.is_active || 1,
        deleted: jobDetails.deleted || 0,
        job_status: jobStatusToSend,
        approval_status: numericalApprovalStatus,
      };

      const response = await AxiosInstance.post(
        "/api/joinourteam/job/update",
        payload,
      );
      console.log("Approval Status updated successfully:", response.data);
      setToast({
        show: true,
        message: "Approval status updated successfully!",
        variant: "success",
      });
      fetchJobDetails(jobDetails.job_id);
      handleCloseApprovalStatusModal();
    } catch (error) {
      console.error("Error updating approval status:", error);
      setToast({
        show: true,
        message: `Failed to update approval status: ${
          error.response?.data?.message || error.message
        }`,
        variant: "danger",
      });
    }
  };

  const handleApprovalStatusRadioChange = (e) => {
    setSelectedApprovalStatus(e.target.value);
  };

  // Other Handlers
  const handleApplicantViewClick = useCallback(
    (rowData) => {
      navigate("/admin/candidates-view", {
        state: {
          email: rowData.email,
          job_id: jobId || "",
          from: "jobpage", // indicates the page we came from
          filters: filters, // ✅ send current filter state to the view page
        },
      });
    },
    [navigate, jobId, filters], // ✅ add filters as dependency
  );

  // const handleApplicantViewClick = useCallback(
  //   (rowData) => {
  //     navigate("/admin/candidates-view", {
  //       state: {
  //         email: rowData.email,
  //         job_id: jobId || "",
  //         from: jobpage, // indicates the page we came from
  //       },
  //     });
  //   },
  //   [navigate, jobId ,jobpage]
  // );

  const actionIconProps = useMemo(
    () => ({
      defaultIcon: "",
      hoverIcon: "",
      onClick: handleApplicantViewClick,
    }),
    [handleApplicantViewClick],
  );

  const handleEditJobClick = () => {
    if (jobDetails && jobDetails.job_id) {
      navigate("/admin/job-create", {
        state: { jobId: jobDetails.job_id, editJob: true },
      });
    } else {
      console.warn("Job ID not available for editing.");
    }
  };

  // Button disabled states
  const isJobStatusButtonDisabled =
    jobDetails?.job_status === 0 || jobDetails?.job_status === 1;

  const isApprovalStatusButtonDisabled =
    jobDetails?.approval_status === 2 || jobDetails?.approval_status === 3;

  return (
    <div>
      <ToastMessage
        show={toast.show}
        onClose={() => setToast({ ...toast, show: false })}
        message={toast.message}
        variant={toast.variant}
      />

      <Card className="p-4 cardShadow">
        <div className="mb-3">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start">
            <h5
              className="mb-3 mb-md-0"
              style={{
                borderLeft: "4px solid orange",
                paddingLeft: "10px",
                color: "#142454",
                fontWeight: "600",
              }}
            >
              Job Details
            </h5>

            <div className={stylesview.buttonRow}>
              <Buttons
                text="Change Job Status"
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleShowJobStatusModal}
                disabled={isJobStatusButtonDisabled}
              />

              {user_type_cookie === "1" && (
                <Buttons
                  text="Change Approval Status"
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleShowApprovalStatusModal}
                  disabled={isApprovalStatusButtonDisabled}
                />
              )}

              {(user_type_cookie === "1" ||
                (user_type_cookie === "2" &&
                  user_type_cookie === jobDetails?.user_type?.toString())) && (
                <Buttons
                  icon={<i className="bi bi-pencil-square me-1" />}
                  text="Edit Job"
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleEditJobClick}
                  disabled={jobDetails?.job_status === 1}
                  className={
                    jobDetails?.job_status === 1 ? "disabled-button" : ""
                  }
                />
              )}
            </div>
          </div>
        </div>

        {jobDetails ? (
          <>
            <Row className="my-3">
              <Col md={4}>
                <div className="staticHeader">Job ID</div>
                <div className="apiData">{jobDetails.job_id || "-"}</div>
              </Col>
              <Col md={4}>
                <div className="staticHeader">Role</div>
                <div className="apiData">{jobDetails.role || "-"}</div>
              </Col>
              <Col md={4}>
                <div className="staticHeader">Location</div>
                <div className="apiData">{`${jobDetails.location || "-"}`}</div>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}>
                <div className="staticHeader">Years of Experience</div>
                <div className="apiData">
                  {jobDetails.yearof_experience || "-"}
                </div>
              </Col>
              <Col md={4}>
                <div className="staticHeader">Work Arrangement</div>
                <div className="apiData">
                  {getWorkArrangementLabel(jobDetails.work_arrangement)}
                </div>
              </Col>
              <Col md={4}>
                <div className="staticHeader">Posted Date</div>
                <div className="apiData">
                  <div>
                    {jobDetails.posted_date
                      ? moment(jobDetails.posted_date).format("DD/MM/YYYY")
                      : "-"}
                  </div>
                </div>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}>
                <div className="staticHeader">Skills Set</div>
                <div className="apiData">{jobDetails.tag_name || "-"} </div>
              </Col>
              <Col md={4}>
                <div className="staticHeader">Department</div>
                <div className="apiData">
                  {jobDetails.department_name || "-"}
                </div>
              </Col>
              <Col md={4}>
                <div className="staticHeader">Job Status</div>
                <div className="apiData">
                  {jobDetails.job_status !== undefined
                    ? ["Waiting for Approval", "Inactive", "Active"][
                        jobDetails.job_status
                      ]
                    : "-"}
                </div>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}>
                <div className="staticHeader">Approval Status</div>
                <div className="apiData">
                  {jobDetails.approval_status !== undefined
                    ? ["Waiting for Approval", "On Hold", "Approved", "Reject"][
                        jobDetails.approval_status
                      ]
                    : "-"}
                </div>
              </Col>
            </Row>
          </>
        ) : (
          <p>Loading job details...</p>
        )}
      </Card>

      <Accordion defaultActiveKey="0" className="mt-3 cardShadow">
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <h5>
              {" "}
              <span
                style={{
                  borderLeft: "4px solid orange",
                  paddingLeft: "10px",
                  fontWeight: "600",
                  color: "#142454",
                }}
              >
                Job Description
              </span>
            </h5>
          </Accordion.Header>
          <Accordion.Body>
            <div className="staticHeader p-2">Job Description</div>
            {jobDetails ? (
              <p className="apiData p-2" style={{ whiteSpace: "pre-line" }}>
                {jobDetails.description || "No description available."}
              </p>
            ) : (
              <p>Loading job description...</p>
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Card className="p-4 cardShadow mt-3">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <h5
            className="mb-3"
            style={{
              borderLeft: "4px solid orange",
              paddingLeft: "10px",
              color: "#142454",
              fontWeight: "600",
            }}
          >
            Applied Candidate List
          </h5>
        </div>
        <div className={`container-fluid px-3 ${Styles.main_Container}`}>
          <div className="row mb-3">
            <div className="col-12 col-md-6">
              <SearchInput
                handleKeyDown={handleSearch}
                placeholder="Search with Name, Email, Phone..."
                initialValue={filters.search}
              />
            </div>
          </div>

          <div className="mb-3">
            {chipData.map((chip, index) => (
              <span key={index} className="custom-badge me-2">
                {chip.label || chip.value}
                <button
                  onClick={() => handleDeleteChip(chip)}
                  className="btn-close ms-2"
                ></button>
              </span>
            ))}
            {(chipData.length > 0 || filters.search) && (
              <button
                className="btn btn-sm btn-outline-danger ms-2"
                onClick={handleAllClear}
              >
                Clear All Filters
              </button>
            )}
          </div>
          <DataTable
            columns={columns}
            data={processedApplicants}
            filterOptions={filterOptions}
            filters={{
              status: filters.candidateStatus,
              willing_to_relocate: filters.willingToRelocate,
              applied_date_from: filters.fromDate,
              applied_date_to: filters.toDate,
            }}
            actionIcon={actionIconProps}
            handleFilter={handleFilterClick}
            isOpens={isOpen}
            filterPopupRef={filterRef}
            currentDateRange={
              isOpen.status && isOpen.column === "applied_date"
                ? {
                    fromDate: tempFilters.fromDate,
                    toDate: tempFilters.toDate,
                    label: tempFilters.dateLabel,
                  }
                : null
            }
            onDateRangeApply={handleDateRangeApply}
            tempCategoricalFilters={{
              status: tempFilters.candidateStatus,
              willing_to_relocate: tempFilters.willingToRelocate,
            }}
            onCategoricalFilterChange={(column, value) => {
              if (column === "status") {
                setTempFilters((prev) => ({ ...prev, candidateStatus: value }));
              }
              if (column === "willing_to_relocate") {
                setTempFilters((prev) => ({
                  ...prev,
                  willingToRelocate: value,
                }));
              }
            }}
            handleApplyFilter={handleApplyFilter}
            onClearSpecificFilter={handleClearSpecificFilter}
            ellipsisIndex={[2, 5]}
          />
          <div
            className="mb-3"
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Pagination
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              totalItems={totalFilteredItems}
              rowsPerPageOptions={rowsPerPageOptions}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={setRowsPerPage}
            />
          </div>
        </div>
      </Card>

      {/* Change Job Status Modal */}
      <Modal show={showJobStatusModal} onHide={handleCloseJobStatusModal}>
        <Modal.Header closeButton>
          <Modal.Title>Change Job Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {["Inactive"].map((status) => (
              <Form.Check
                key={status}
                type="radio"
                label={status}
                name="jobStatus"
                id={`jobStatus-${status}`}
                value={status}
                checked={selectedJobStatus === status}
                onChange={handleJobStatusRadioChange}
                className="mb-2 custom-radio"
              />
            ))}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Buttons
            text="Cancel"
            type="button"
            variant="secondary"
            onClick={handleCloseJobStatusModal}
          />
          <Buttons
            text="Save"
            type="button"
            variant="primary"
            onClick={handleSaveJobStatus}
          />
        </Modal.Footer>
      </Modal>

      {/* Change Approval Status Modal */}
      <Modal
        show={showApprovalStatusModal}
        onHide={handleCloseApprovalStatusModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Approval Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {["Waiting for Approval", "On Hold", "Approved", "Reject"].map(
              (status) => (
                <Form.Check
                  key={status}
                  type="radio"
                  label={status}
                  name="approvalStatus"
                  id={`approvalStatus-${status}`}
                  value={status}
                  checked={selectedApprovalStatus === status}
                  onChange={handleApprovalStatusRadioChange}
                  className="mb-2 custom-radio"
                />
              ),
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Buttons
            text="Cancel"
            type="button"
            onClick={handleCloseApprovalStatusModal}
            variant="secondary"
          />
          <Buttons
            text="Save"
            type="button"
            variant="primary"
            onClick={handleSaveApprovalStatus}
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Job_View;
